import { CompanyDailyNavConfigComp, DailyNavGPCApproach } from 'api';
import { CapIQ, CapIQCompanyData } from 'pages/ValuationsAllocation/types';

// Draft shape for the comps watchlist. The API's readonly CompanyDailyNavConfigComp adds
// an `order` field we don't need locally; the write payload matches this shape exactly.
export type CompRow = {
  comp_type: CompanyDailyNavConfigComp.comp_type;
  cap_iq_id: string;
  ticker_symbol: string;
  company_name: string;
  // Resolved as the comp is added so the save carries it. Null while the check
  // is in flight, or when Alexandria does not have the company yet.
  alexandria_id: string | null;
};

export const COMP_TYPE_LABELS: Record<CompanyDailyNavConfigComp.comp_type, string> = {
  [CompanyDailyNavConfigComp.comp_type.PUBLIC_COMP]: 'Public Comp',
  [CompanyDailyNavConfigComp.comp_type.ETF]: 'ETF',
};

export const toCompRow = (comp: {
  comp_type: CompanyDailyNavConfigComp.comp_type;
  cap_iq_id: string;
  ticker_symbol: string;
  company_name: string;
  alexandria_id?: string | null;
}): CompRow => ({
  comp_type: comp.comp_type,
  cap_iq_id: comp.cap_iq_id,
  ticker_symbol: comp.ticker_symbol,
  company_name: comp.company_name,
  alexandria_id: comp.alexandria_id ?? null,
});

// Cap IQ fields come wrapped in a { value } envelope; extract to a plain string.
export const capIqFieldToString = (field?: CapIQ): string =>
  field?.value === null || field?.value === undefined ? '' : String(field.value);

// Cap IQ classifies ETFs and index funds as "public fund" / "index"; everything
// else searched via the comparable-company picker is treated as a public comp.
const ETF_COMPANY_TYPES = ['public fund', 'index'];
export const inferCompType = (companyType: string): CompanyDailyNavConfigComp.comp_type =>
  ETF_COMPANY_TYPES.includes(companyType.trim().toLowerCase())
    ? CompanyDailyNavConfigComp.comp_type.ETF
    : CompanyDailyNavConfigComp.comp_type.PUBLIC_COMP;

// A GPC approach's comps as watchlist rows. GPC holds public comps only.
export const gpcApproachToCompRows = (approach: DailyNavGPCApproach): CompRow[] =>
  approach.comps
    .filter(comp => Boolean(comp.cap_iq_id))
    .map(comp => ({
      comp_type: CompanyDailyNavConfigComp.comp_type.PUBLIC_COMP,
      cap_iq_id: comp.cap_iq_id as string,
      ticker_symbol: comp.ticker_symbol ?? '',
      company_name: comp.company_name ?? '',
      alexandria_id: null,
    }));

// AddGpcDialog's picks as watchlist rows, inferring ETF vs public comp from company_type.
export const capIqSelectionToCompRows = (data: CapIQCompanyData[]): CompRow[] =>
  data
    .map(item => ({
      comp_type: inferCompType(capIqFieldToString(item.gpc?.company_type)),
      cap_iq_id: capIqFieldToString(item.gpc?.cap_iq_id),
      // Verbatim, exchange prefix included: a bare symbol is not a unique key,
      // and Alexandria matches the qualified form exactly.
      ticker_symbol: capIqFieldToString(item.gpc?.ticker_symbol),
      company_name: capIqFieldToString(item.gpc?.company_name),
      alexandria_id: null,
    }))
    .filter(row => Boolean(row.cap_iq_id));

// Order matters: the save persists the list's order.
export const compListsDiffer = (saved: CompRow[], draft: CompRow[]) =>
  saved.length !== draft.length ||
  saved.some((savedComp, index) => {
    const draftComp = draft[index];
    return (
      !draftComp ||
      draftComp.comp_type !== savedComp.comp_type ||
      draftComp.cap_iq_id !== savedComp.cap_iq_id ||
      draftComp.ticker_symbol !== savedComp.ticker_symbol ||
      draftComp.company_name !== savedComp.company_name
    );
  });
